import { useState } from "react";
import { CircleHelpIcon } from "lucide-react";
import { Tooltip } from "@/components/Common/Tooltip";
import { useStore } from "@/stores/useStore";
import { useDispatchStats } from "@/hooks/useDispatchStats";
import { useTechnicians } from "@/hooks/shop/useTechnicians";
import { TechnicianHoursSummary } from "./TechnicianHoursSummary";
import { TechnicianHoursTable } from "./TechnicianHoursTable";
import { EmployeeJobsDetail } from "./EmployeeJobsDetail";
import { TECHNICIAN_HOURS_HEADING_TOOLTIP } from "./columnTooltips";

export function DispatchView() {
  const { cards: boardCards } = useStore();
  const technicians = useTechnicians();
  const [selectedEmployee, setSelectedEmployee] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Scheduled hours only; no week mode (per-day/week is out of scope).
  // useTechnicians() already applies the shop's roster setting
  // (useShopHeroEmployeeJobTitle) — do not re-filter its output here.
  const stats = useDispatchStats(boardCards, [], technicians, "board");

  if (stats.technicianBreakdown.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-500">
        No technicians found
      </div>
    );
  }

  if (selectedEmployee) {
    return (
      <EmployeeJobsDetail
        // Must stay the same card set useDispatchStats aggregates from
        // (getCardsForDispatchView) — currently identical since mode is
        // hardcoded "board". If week-mode is ever wired up, this needs to
        // run through the same filtering or the detail view will silently
        // stop matching the row that was clicked.
        cards={boardCards}
        employeeId={selectedEmployee.id}
        employeeName={selectedEmployee.name}
        onBack={() => setSelectedEmployee(null)}
      />
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-y-auto bg-slate-100 p-4">
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-bold text-[#0F172A]">Technician Hours</h2>
        {/* A real button, not a bare icon: the tooltip carries the only
            explanation of what this table counts and how to narrow it, so
            it has to be reachable by keyboard as well as by hover. */}
        <Tooltip
          message={TECHNICIAN_HOURS_HEADING_TOOLTIP}
          delayDuration={200}
          className="max-w-xs whitespace-normal text-xs leading-snug"
        >
          <button
            type="button"
            aria-label="About Technician Hours"
            className="rounded-full text-slate-400 transition-colors hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <CircleHelpIcon size={15} />
          </button>
        </Tooltip>
      </div>

      <TechnicianHoursSummary
        totalApprovedHours={stats.totalHoursByState.approved}
        totalTechnicians={stats.technicianBreakdown.length}
        averageApprovedHours={stats.averageApprovedHoursPerTechnician}
      />

      {/* No card wrapper here: TechnicianHoursTable draws the white card
          around the table itself, so the Columns toolbar above it sits on
          this panel's slate background. */}
      <TechnicianHoursTable
        technicianStats={stats.technicianBreakdown}
        onSelectEmployee={(id, name) => setSelectedEmployee({ id, name })}
      />
    </div>
  );
}
